import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  ModalBody,
  ModalFooter,
  Select,
  SimpleGrid,
  Switch,
  Text,
  Textarea,
  Tooltip,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import {
  trpcAdmin,
  type RouterInputs,
  type RouterOutputs,
} from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { RequirementChip } from '../../../components/requirements/RequirementChip';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';

import {
  RECURRING_ROLES,
  type RequirementRef,
} from '@duke-hq/unit-generation-pipeline/schemas/sectionPlan';

import { testIds } from './testIds';
import {
  BlockersAlert,
  FieldLabel,
  GroupHeading,
  OutlineHeading,
  OutlineItem,
  PendingBody,
  ReviewLayout,
  ReviewShell,
} from './ReviewLayout';
import { RequirementPicker } from '../../../components/requirements/RequirementPicker';
import { FoundationSkillPicker } from '../../../components/requirements/FoundationSkillPicker';
import {
  KIND_COLOURS,
  requirementId,
  requirementLabel,
  UnitRequirementKindHeading,
} from '../../../components/requirements/kinds';
import { hasFixedWorkplace } from '@duke-hq/unit-generation-pipeline/generate/prompts/learnerProfile';

import { blank } from '../reviewedRequirements';

type SectionPlanData = RouterOutputs['unitsOfCompetency']['sectionPlan'];
type SaveInput = RouterInputs['unitsOfCompetency']['saveAndContinueFromAllocation'];
type CastRef = SaveInput['sections'][number]['cast'][number];
type DraftSection = SectionPlanData['sections'][number];

type Draft = Pick<SectionPlanData, 'sections' | 'recurringCast' | 'learnerProfile'>;

type PaneSelection = { kind: 'castAndContext' } | { kind: 'section'; key: string };

const LEARNER_PROFILE_FIELDS = [
  { key: 'jobRole', label: 'Job role', long: false, required: false },
  { key: 'workplace', label: 'Workplace', long: false, required: true },
  { key: 'location', label: 'Location', long: false, required: true },
  { key: 'organisationalContext', label: 'Organisational context', long: true, required: false },
] as const;

function missingWorkplaceFields(learnerProfile: Draft['learnerProfile']) {
  return LEARNER_PROFILE_FIELDS.filter(({ key, required }) => {
    const value = learnerProfile[key];

    return required && (typeof value !== 'string' || blank(value));
  }).map(({ label }) => label);
}

function withoutBlankUsage(member: CastRef): CastRef {
  return {
    personaId: member.personaId,
    name: member.name,
    ...(member.usage == null || blank(member.usage) ? {} : { usage: member.usage }),
  };
}

function submittableMember(member: CastRef | null | undefined): CastRef | null {
  return member == null ? null : withoutBlankUsage(member);
}

function homesOf(draft: Draft): Map<string, string> {
  return new Map(
    draft.sections.flatMap((section) =>
      section.requirements.map((ref) => [requirementId(ref), section.key] as const),
    ),
  );
}

type Persona = SectionPlanData['personas'][number];
type SectionCastMember = DraftSection['cast'][number];

function reviewBlockers(
  plan: SectionPlanData,
  draft: Draft,
  homes: Map<string, string>,
  inWorkplace: boolean,
): string[] {
  const homeless = plan.requirements
    .filter((requirement) => !homes.has(requirementId(requirement)))
    .map(requirementLabel);
  const untitled = draft.sections.flatMap((section, index) =>
    blank(section.title) ? [index + 1] : [],
  );
  const withoutCast = draft.sections.flatMap((section, index) =>
    section.cast.length === 0 ? [index + 1] : [],
  );
  const unassignedRoles = unassignedRecurringRoles(draft, inWorkplace);
  const missingWorkplace = inWorkplace ? missingWorkplaceFields(draft.learnerProfile) : [];

  const blockers: string[] = [];

  if (homeless.length > 0) {
    blockers.push(t`These requirements have no section: ${homeless.join(', ')}.`);
  }

  if (untitled.length > 0) {
    blockers.push(t`Sections without a title: ${untitled.join(', ')}.`);
  }

  if (withoutCast.length > 0) {
    blockers.push(t`Sections without a cast member: ${withoutCast.join(', ')}.`);
  }

  if (unassignedRoles.length > 0) {
    blockers.push(t`Recurring roles still unassigned: ${unassignedRoles.join(', ')}.`);
  }

  if (missingWorkplace.length > 0) {
    blockers.push(t`Set in a workplace needs: ${missingWorkplace.join(', ')}.`);
  }

  return blockers;
}

function unassignedRecurringRoles(draft: Draft, inWorkplace: boolean) {
  const required = inWorkplace ? (['guide', 'supervisor'] as const) : (['guide'] as const);

  return required.filter((role) => draft.recurringCast[role] == null);
}

function toPayload(
  runId: string,
  plan: SectionPlanData,
  draft: Draft,
  inWorkplace: boolean,
): SaveInput | null {
  const guide = submittableMember(draft.recurringCast.guide);

  if (
    guide == null ||
    unassignedRecurringRoles(draft, inWorkplace).length > 0 ||
    (inWorkplace && missingWorkplaceFields(draft.learnerProfile).length > 0)
  ) {
    return null;
  }

  return {
    id: runId,
    etag: plan.etag,
    sections: draft.sections.map((section) => ({
      key: section.key,
      title: section.title,
      description: section.description,
      requirements: section.requirements,
      foundationSkills: section.foundationSkills,
      cast: section.cast.map(withoutBlankUsage),
    })),
    recurringCast: {
      guide,
      peer: submittableMember(draft.recurringCast.peer),
      supervisor: hasFixedWorkplace(draft.learnerProfile)
        ? submittableMember(draft.recurringCast.supervisor)
        : null,
    },
    learnerProfile: draft.learnerProfile,
  };
}

function CastRow({
  member,
  options,
  label,
  canRemove,
  onChange,
  onRemove,
}: {
  member: SectionCastMember;
  options: Persona[];
  label: string;
  canRemove: boolean;
  onChange: (member: SectionCastMember) => void;
  onRemove: () => void;
}) {
  return (
    <HStack spacing={2}>
      <Select
        size="sm"
        w={pxToRem(180)}
        value={member.personaId}
        aria-label={label}
        onChange={(event) => {
          const persona = options.find(({ id }) => id === event.target.value);

          if (persona != null) {
            onChange({ ...member, personaId: persona.id, name: persona.name });
          }
        }}
      >
        {options.map((persona) => (
          <option key={persona.id} value={persona.id}>
            {persona.roleTitle == null ? persona.name : `${persona.name} (${persona.roleTitle})`}
          </option>
        ))}
      </Select>

      <Input
        size="sm"
        value={member.usage ?? ''}
        placeholder={t`How they are used here`}
        aria-label={t`Usage for ${member.name}`}
        onChange={(event) => onChange({ ...member, usage: event.target.value })}
      />

      <Tooltip label={t`A section needs at least one cast member.`} isDisabled={canRemove}>
        <IconButton
          size="sm"
          variant="ghost"
          colorScheme="red"
          isDisabled={!canRemove}
          data-testid={testIds.removeCastButton}
          aria-label={t`Remove ${member.name}`}
          icon={<Trash2 size={iconSize.sm} />}
          onClick={onRemove}
        />
      </Tooltip>
    </HStack>
  );
}

function CastEditor({
  cast,
  personas,
  sectionNumber,
  onChange,
}: {
  cast: SectionCastMember[];
  personas: Persona[];
  sectionNumber: number;
  onChange: (cast: SectionCastMember[]) => void;
}) {
  const unused = personas.filter(({ id }) => !cast.some(({ personaId }) => personaId === id));

  return (
    <VStack align="stretch" spacing={2}>
      {cast.map((member, index) => (
        <CastRow
          key={member.personaId}
          member={member}
          options={personas.filter(
            ({ id }) => id === member.personaId || unused.some((free) => free.id === id),
          )}
          label={t`Cast member ${index + 1} in section ${sectionNumber}`}
          canRemove={cast.length > 1}
          onChange={(next) => onChange(cast.map((current, at) => (at === index ? next : current)))}
          onRemove={() => onChange(cast.filter((_, at) => at !== index))}
        />
      ))}

      <Button
        size="xs"
        variant="ghost"
        alignSelf="flex-start"
        isDisabled={unused.length === 0}
        leftIcon={<Plus size={iconSize.xs} />}
        data-testid={testIds.addCastButton}
        onClick={() => {
          const persona = unused[0];

          if (persona != null) {
            onChange([...cast, { personaId: persona.id, name: persona.name }]);
          }
        }}
      >
        <Trans>Add cast member</Trans>
      </Button>
    </VStack>
  );
}

function SectionCard({
  section,
  number,
  plan,
  byCode,
  homes,
  ownerTags,
  onPatch,
  onMoveRequirement,
}: {
  section: DraftSection;
  number: number;
  plan: SectionPlanData;
  byCode: Map<string, SectionPlanData['requirements'][number]>;
  homes: Map<string, string>;
  ownerTags: Map<string, string>;
  onPatch: (patch: Partial<DraftSection>) => void;
  onMoveRequirement: (requirement: RequirementRef, toKey: string | null) => void;
}) {
  const homeOf = (requirement: RequirementRef) => homes.get(requirementId(requirement));

  const requirementGroups = [
    {
      key: 'here',
      label: <Trans>In this section</Trans>,
      rows: plan.requirements.filter((requirement) => homeOf(requirement) === section.key),
      greyed: false,
      linked: true,
    },
    {
      key: 'available',
      label: <Trans>Available</Trans>,
      rows: plan.requirements.filter((requirement) => homeOf(requirement) == null),
      greyed: false,
      linked: false,
    },
    {
      key: 'elsewhere',
      label: <Trans>Allocated elsewhere</Trans>,
      rows: plan.requirements.filter((requirement) => {
        const home = homeOf(requirement);
        return home != null && home !== section.key;
      }),
      greyed: true,
      linked: false,
    },
  ];

  return (
    <VStack align="stretch" spacing={5} data-testid={testIds.sectionCard}>
      <Box>
        <Box mb={2}>
          <GroupHeading>
            <Trans>Section</Trans>
          </GroupHeading>
        </Box>

        <FieldLabel>
          <Trans>Title</Trans>
        </FieldLabel>

        <HStack spacing={2} align="center">
          <Badge colorScheme="gray" fontSize="sm" minW={pxToRem(28)} textAlign="center">
            {number}
          </Badge>
          <Input
            size="sm"
            fontWeight="medium"
            value={section.title}
            placeholder={t`Section title`}
            aria-label={t`Title for section ${number}`}
            onChange={(event) => onPatch({ title: event.target.value })}
          />
        </HStack>

        <Box mt={3}>
          <FieldLabel>
            <Trans>Description</Trans>
          </FieldLabel>
          <Textarea
            size="sm"
            rows={3}
            value={section.description}
            placeholder={t`What this section covers`}
            aria-label={t`Description for section ${number}`}
            onChange={(event) => onPatch({ description: event.target.value })}
          />
        </Box>
      </Box>

      <Divider />

      <Box>
        <Flex justify="space-between" align="center" mb={2}>
          <GroupHeading>
            <Trans>Requirements</Trans>
          </GroupHeading>

          <RequirementPicker
            groups={requirementGroups}
            triggerLabel={<Trans>Allocate requirements</Trans>}
            triggerIcon={Plus}
            triggerProps={{ 'data-testid': testIds.allocateRequirementsButton }}
            optionTestId={testIds.requirementOption}
            idOf={requirementId}
            colourOf={({ kind }) => KIND_COLOURS[kind]}
            holderTagOf={(requirement) => {
              const home = homeOf(requirement);
              return home != null && home !== section.key ? ownerTags.get(home) : undefined;
            }}
            onToggle={(requirement, linked) =>
              onMoveRequirement(requirement, linked ? section.key : null)
            }
          />
        </Flex>

        <Flex gap={2} wrap="wrap" align="center">
          {section.requirements.map((requirement) => (
            <RequirementChip
              key={requirementId(requirement)}
              colourScheme={KIND_COLOURS[requirement.kind]}
              label={requirement.code}
              description={byCode.get(requirementId(requirement))?.description ?? ''}
              removeLabel={t`Unallocate ${requirement.code}`}
              onRemove={() => onMoveRequirement(requirement, null)}
              data-testid={testIds.requirementChip}
            />
          ))}
        </Flex>

        <Box mt={3}>
          <FieldLabel>
            <UnitRequirementKindHeading kind="FOUNDATION_SKILL" />
          </FieldLabel>

          <FoundationSkillPicker
            options={plan.foundationSkillOptions}
            selected={section.foundationSkills}
            onToggle={(category, next) =>
              onPatch({
                foundationSkills: next
                  ? [...section.foundationSkills, category]
                  : section.foundationSkills.filter((skill) => skill !== category),
              })
            }
          />
        </Box>
      </Box>

      <Divider />

      <Box>
        <Box mb={2}>
          <GroupHeading>
            <Trans>Cast</Trans>
          </GroupHeading>
        </Box>

        <CastEditor
          cast={section.cast}
          personas={plan.personas}
          sectionNumber={number}
          onChange={(cast) => onPatch({ cast })}
        />
      </Box>
    </VStack>
  );
}

function CastAndContextCard({
  recurringCast,
  learnerProfile,
  personas,
  inWorkplace,
  onToggleWorkplace,
  onPatch,
}: {
  recurringCast: Draft['recurringCast'];
  learnerProfile: Draft['learnerProfile'];
  personas: Persona[];
  inWorkplace: boolean;
  onToggleWorkplace: (next: boolean) => void;
  onPatch: (patch: Partial<Pick<Draft, 'recurringCast' | 'learnerProfile'>>) => void;
}) {
  return (
    <VStack align="stretch" spacing={5} flex={1} minH={0}>
      <Box>
        <Box mb={2}>
          <GroupHeading>
            <Trans>Recurring cast</Trans>
          </GroupHeading>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
          {RECURRING_ROLES.filter((role) => inWorkplace || role !== 'supervisor').map((role) => (
            <FormControl key={role}>
              <FormLabel fontSize="xs" textTransform="capitalize" mb={1}>
                {role}
              </FormLabel>
              <Select
                size="sm"
                value={recurringCast[role]?.personaId ?? ''}
                aria-label={t`Recurring ${role}`}
                onChange={(event) => {
                  const persona = personas.find(({ id }) => id === event.target.value);

                  if (persona !== undefined) {
                    onPatch({
                      recurringCast: {
                        ...recurringCast,
                        [role]: { personaId: persona.id, name: persona.name },
                      },
                    });
                  }
                }}
              >
                {role === 'peer' ? <option value="">{t`No recurring peer`}</option> : null}
                {role === 'supervisor' && recurringCast.supervisor == null ? (
                  <option value="">{t`Choose a supervisor`}</option>
                ) : null}

                {personas.map((persona) => (
                  <option key={persona.id} value={persona.id}>
                    {persona.name}
                  </option>
                ))}
              </Select>
            </FormControl>
          ))}
        </SimpleGrid>
      </Box>

      <Divider />

      <Box flex={1} minH={0} display="flex" flexDirection="column">
        <HStack mb={2} justify="space-between" align="center">
          <GroupHeading>
            <Trans>Learner profile</Trans>
          </GroupHeading>
          <HStack as="label" spacing={2} align="center" cursor="pointer">
            <Text fontSize="xs" fontWeight="semibold" color="fg.muted" lineHeight="1">
              <Trans>Set in a workplace</Trans>
            </Text>
            <Switch
              size="sm"
              isChecked={inWorkplace}
              data-testid={testIds.workplaceSwitch}
              onChange={(event) => onToggleWorkplace(event.target.checked)}
            />
          </HStack>
        </HStack>

        {inWorkplace ? (
          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacing={3}
            flex={1}
            minH={0}
            templateRows={{ md: 'auto auto minmax(0, 1fr)' }}
          >
            {LEARNER_PROFILE_FIELDS.map(({ key, label, long, required }) => {
              const value = learnerProfile[key];
              const shared = {
                size: 'sm' as const,
                value: typeof value === 'string' ? value : '',
                'aria-label': label,
              };
              const patchField = (next: string) =>
                onPatch({ learnerProfile: { ...learnerProfile, [key]: next } });

              return (
                <FormControl
                  key={key}
                  isRequired={required}
                  gridColumn={long ? { md: 'span 2' } : undefined}
                  display={long ? 'flex' : undefined}
                  flexDirection="column"
                  minH={0}
                >
                  <FormLabel fontSize="xs" mb={1}>
                    {label}
                  </FormLabel>

                  {long ? (
                    <Textarea
                      {...shared}
                      flex={1}
                      minH={pxToRem(120)}
                      resize="none"
                      onChange={(event) => patchField(event.target.value)}
                    />
                  ) : (
                    <Input {...shared} onChange={(event) => patchField(event.target.value)} />
                  )}
                </FormControl>
              );
            })}
          </SimpleGrid>
        ) : null}
      </Box>
    </VStack>
  );
}

export function AllocationReviewModal({
  runId,
  isOpen,
  onClose,
}: {
  runId: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const utils = trpcAdmin.useUtils();
  const [edits, setEdits] = useState<Draft | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [selected, setSelected] = useState<PaneSelection>({ kind: 'castAndContext' });
  const [workplaceOverride, setWorkplaceOverride] = useState<boolean | null>(null);
  const [clearedWorkplace, setClearedWorkplace] = useState<Pick<
    Draft,
    'learnerProfile' | 'recurringCast'
  > | null>(null);

  const { data: plan, error } = trpcAdmin.unitsOfCompetency.sectionPlan.useQuery(
    { id: runId },
    { enabled: isOpen, refetchOnWindowFocus: false, staleTime: Infinity },
  );

  const isDirty = edits != null;

  const saveMutation = trpcAdmin.unitsOfCompetency.saveAndContinueFromAllocation.useMutation({
    onSuccess: () => {
      toast({ status: 'success', title: t`Generating assessments` });
      void utils.unitsOfCompetency.listGenerations.invalidate();
      void utils.unitsOfCompetency.generationById.invalidate({ id: runId });
      onClose();
    },
    onError: (mutationError) => {
      if (mutationError.data?.code === 'CONFLICT') {
        void utils.unitsOfCompetency.sectionPlan.invalidate({ id: runId });
      }

      toast({
        status: 'error',
        title: t`Could not continue`,
        description: mutationError.message,
      });
    },
  });

  const requestClose = () => {
    if (isDirty) {
      setConfirmClose(true);
      return;
    }

    onClose();
  };

  if (plan == null) {
    return (
      <ReviewShell isOpen={isOpen} onClose={requestClose} title={<Trans>Review allocation</Trans>}>
        <PendingBody error={error} />
      </ReviewShell>
    );
  }

  const draft: Draft = edits ?? plan;
  const { unitCode } = plan;

  const requirementsByCode = new Map(
    plan.requirements.map((requirement) => [requirementId(requirement), requirement]),
  );

  const homes = homesOf(draft);
  const sectionTags = new Map(
    draft.sections.map((section, index) => [section.key, `S${index + 1}`] as const),
  );
  const activeSectionIndex =
    selected.kind === 'section' ? draft.sections.findIndex(({ key }) => key === selected.key) : -1;

  const activeSection = activeSectionIndex === -1 ? undefined : draft.sections[activeSectionIndex];
  const inWorkplace = workplaceOverride ?? hasFixedWorkplace(draft.learnerProfile);
  const blockers = reviewBlockers(plan, draft, homes, inWorkplace);
  const canContinue = blockers.length === 0;
  const onSave = () => {
    const payload = toPayload(runId, plan, draft, inWorkplace);

    if (payload != null) {
      saveMutation.mutate(payload);
    }
  };

  const patchDraft = (patch: Partial<Draft>) => setEdits({ ...draft, ...patch });

  const toggleWorkplace = (next: boolean) => {
    setWorkplaceOverride(next);

    if (next) {
      if (clearedWorkplace != null) {
        patchDraft(clearedWorkplace);
        setClearedWorkplace(null);
      }
      return;
    }

    setClearedWorkplace({
      learnerProfile: draft.learnerProfile,
      recurringCast: draft.recurringCast,
    });
    patchDraft({
      learnerProfile: Object.fromEntries(LEARNER_PROFILE_FIELDS.map(({ key }) => [key, ''])),
      recurringCast: { ...draft.recurringCast, supervisor: null },
    });
  };

  const patchActiveSection = (patch: Partial<DraftSection>) =>
    setEdits({
      ...draft,
      sections: draft.sections.map((current, at) =>
        at === activeSectionIndex ? { ...current, ...patch } : current,
      ),
    });

  const moveRequirement = (requirement: RequirementRef, toKey: string | null) => {
    const id = requirementId(requirement);

    setEdits({
      ...draft,
      sections: draft.sections.map((section) => {
        const without = section.requirements.filter((ref) => requirementId(ref) !== id);

        return section.key === toKey
          ? { ...section, requirements: [...without, requirement] }
          : { ...section, requirements: without };
      }),
    });
  };

  return (
    <ReviewShell
      isOpen={isOpen}
      onClose={requestClose}
      title={<Trans>Review allocation</Trans>}
      subtitle={<Trans>Sections, cast and requirement allocation for {unitCode}.</Trans>}
    >
      <ModalBody display="flex" flexDirection="column" overflow="hidden">
        <ReviewLayout
          outline={
            <>
              <OutlineItem
                isSelected={selected.kind === 'castAndContext'}
                hasIssue={
                  unassignedRecurringRoles(draft, inWorkplace).length > 0 ||
                  (inWorkplace && missingWorkplaceFields(draft.learnerProfile).length > 0)
                }
                label={<Trans>Recurring cast & context</Trans>}
                onSelect={() => setSelected({ kind: 'castAndContext' })}
              />

              <Divider my={2} />

              <OutlineHeading>
                <Trans>Sections</Trans>
              </OutlineHeading>

              {draft.sections.map((section, index) => (
                <OutlineItem
                  key={section.key}
                  isSelected={selected.kind === 'section' && selected.key === section.key}
                  hasIssue={blank(section.title) || section.cast.length === 0}
                  badge={
                    <Badge
                      colorScheme="gray"
                      fontSize="xs"
                      flexShrink={0}
                      minW={pxToRem(24)}
                      textAlign="center"
                    >
                      {index + 1}
                    </Badge>
                  }
                  count={section.requirements.length}
                  label={blank(section.title) ? t`Untitled section` : section.title}
                  onSelect={() => setSelected({ kind: 'section', key: section.key })}
                />
              ))}
            </>
          }
        >
          {activeSection != null ? (
            <SectionCard
              key={activeSection.key}
              section={activeSection}
              number={activeSectionIndex + 1}
              plan={plan}
              byCode={requirementsByCode}
              homes={homes}
              ownerTags={sectionTags}
              onPatch={patchActiveSection}
              onMoveRequirement={moveRequirement}
            />
          ) : (
            <CastAndContextCard
              recurringCast={draft.recurringCast}
              learnerProfile={draft.learnerProfile}
              personas={plan.personas}
              inWorkplace={inWorkplace}
              onToggleWorkplace={toggleWorkplace}
              onPatch={patchDraft}
            />
          )}
        </ReviewLayout>
      </ModalBody>

      <ModalFooter gap={2}>
        {blockers.length > 0 ? (
          <BlockersAlert status="error" messages={blockers} data-testid={testIds.coverageWarning} />
        ) : null}

        <Button variant="secondary" onClick={requestClose}>
          <Trans>Close</Trans>
        </Button>

        <Button
          colorScheme="brand"
          isDisabled={!canContinue}
          isLoading={saveMutation.isPending}
          data-testid={testIds.saveAndContinueButton}
          onClick={onSave}
        >
          {plan.isRetry ? <Trans>Retry assessments</Trans> : <Trans>Generate assessments</Trans>}
        </Button>
      </ModalFooter>

      <ConfirmationDialog
        isOpen={confirmClose}
        onClose={() => setConfirmClose(false)}
        title={<Trans>Discard your changes?</Trans>}
        body={
          <Text>
            <Trans>This allocation has unsaved edits. Closing loses them.</Trans>
          </Text>
        }
        onConfirm={() => {
          setConfirmClose(false);
          onClose();
        }}
        confirmLabel={<Trans>Discard</Trans>}
        cancelLabel={<Trans>Keep editing</Trans>}
        variant="destructive"
      />
    </ReviewShell>
  );
}
